import { Stack, Typography } from "@mui/material";
import AnalystResponseCard from "./AnalystResponseCard";
import { Article, Recommend, CheckCircle } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "motion/react";
import { extractStringValue, extractArrayItems, parsePartialJSON } from "./utils/jsonParsingUtils";
import { useResponseFallback } from "./utils/responseUtils";

interface AnalystResponseProps {
  response: string;
}

interface AnalystResponseData {
  analysis_summary?: {
    key_callout?: string;
    summary_text?: string;
    critical_insights?: string[];
  };
  key_recommendations?: string[];
}

// Helper function to extract partial analyst response fields
const extractAnalystPartialFields = (jsonString: string): Partial<AnalystResponseData> | null => {
  const partial: Partial<AnalystResponseData> = {};

  // Extract fields from analysis_summary
  const analysisSummaryMatch = jsonString.match(/"analysis_summary"\s*:\s*\{/);
  if (analysisSummaryMatch) {
    partial.analysis_summary = {};

    // Extract key_callout
    const keyCallout = extractStringValue(jsonString, 'key_callout');
    if (keyCallout !== null) {
      partial.analysis_summary.key_callout = keyCallout;
    }

    // Extract summary_text
    const summaryText = extractStringValue(jsonString, 'summary_text');
    if (summaryText !== null) {
      partial.analysis_summary.summary_text = summaryText;
    }

    // Extract critical_insights
    const insights = extractArrayItems(jsonString, 'critical_insights');
    if (insights.length > 0) {
      partial.analysis_summary.critical_insights = insights;
    }
  }

  // Extract key_recommendations
  const recommendations = extractArrayItems(jsonString, 'key_recommendations');
  if (recommendations.length > 0) {
    partial.key_recommendations = recommendations;
  }

  return Object.keys(partial).length > 0 || (partial.analysis_summary && Object.keys(partial.analysis_summary).length > 0) ? partial : null;
};

function AnalystResponse({ response }: AnalystResponseProps) {
  const theme = useTheme();
  const firstJSONObjectIndex = response.indexOf("{");
  const prefixedInsight =
    firstJSONObjectIndex > 0
      ? response.slice(0, firstJSONObjectIndex).trim()
      : "";
  const jsonPart =
    firstJSONObjectIndex >= 0 ? response.slice(firstJSONObjectIndex) : response;

  const analystData = parsePartialJSON<AnalystResponseData>(jsonPart, extractAnalystPartialFields);
  const fallback = useResponseFallback({ response, parsedData: analystData });

  if (fallback.render || !analystData) {
    return fallback.render || <></>;
  }

  const hasAnalysisSummary =
    analystData.analysis_summary?.key_callout ||
    analystData.analysis_summary?.summary_text ||
    (analystData.analysis_summary?.critical_insights && analystData.analysis_summary.critical_insights.length > 0);

  const vibrantColor = theme.palette.primary.vibrant;
  const mainColor = theme.palette.primary.main;

  return (
    <Stack spacing={2}>
      <AnimatePresence>
        {prefixedInsight && (
          <motion.div
            key="prefixed-insight"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
          >
            <AnalystResponseCard
              title="Insight"
              content={
                <Typography variant="body2">
                  {prefixedInsight}
                </Typography>
              }
              icon={<Article sx={{ color: mainColor }} />}
              borderColor={mainColor}
            />
          </motion.div>
        )}

        {hasAnalysisSummary && (
          <motion.div
            key="analysis-summary"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
          >
            <AnalystResponseCard
              title="Analysis Summary"
              content={
                <Stack spacing={2}>
                  {analystData.analysis_summary?.key_callout && (
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {analystData.analysis_summary.key_callout}
                    </Typography>
                  )}

                  {analystData.analysis_summary?.summary_text && (
                    <Typography variant="body2">
                      {analystData.analysis_summary.summary_text}
                    </Typography>
                  )}

                  {analystData.analysis_summary?.critical_insights && analystData.analysis_summary.critical_insights.length > 0 && (
                    <Stack spacing={1} sx={{ m: 0 }}>
                      {analystData.analysis_summary.critical_insights.map((insight, index) => (
                        <Stack key={index} direction="row" alignItems="center" spacing={1}>
                          <CheckCircle fontSize="extraSmall" sx={{ color: mainColor, flexShrink: 0 }} />
                          <Typography variant="body2">
                            {insight}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              }
              icon={<Article sx={{ color: mainColor }} />}
              borderColor={mainColor}
            />
          </motion.div>
        )}

        {analystData.key_recommendations && analystData.key_recommendations.length > 0 && (
          <motion.div
            key="key-recommendations"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              delay: 0.1,
            }}
          >
            <AnalystResponseCard
              title="Key Recommendations"
              content={
                <Stack spacing={1} sx={{ m: 0 }}>
                  {analystData.key_recommendations.map((recommendation, index) => (
                    <Stack key={index} direction="row" alignItems="center" spacing={1}>
                      <CheckCircle fontSize="extraSmall" sx={{ color: vibrantColor, flexShrink: 0 }} />
                      <Typography variant="body2">
                        {recommendation}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              }
              icon={<Recommend sx={{ color: vibrantColor }} />}
              borderColor={vibrantColor}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
}

export default AnalystResponse;
